import { styled } from 'styled-components';
import React, {
  FC,
  DragEventHandler,
  ChangeEventHandler,
  useRef,
  useEffect,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faPaperclip } from '@fortawesome/free-solid-svg-icons/faPaperclip';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Styling } from '@/styling';
import { Divider } from '@/components/visual/Divider';
import { Box } from '@/components/layout/Box';

const Container = styled.div``;

type DropAreaProps = {
  isDragging?: boolean;
};

const DropArea = styled.div<DropAreaProps>`
  position: relative;
  width: 100%;
  height: 100px;
  padding: ${Styling.spacing(2)};
  border: 2px dashed
    ${({ theme, isDragging }) =>
      isDragging ? theme.palette.primary.main : '#E9E9E9'};
  background-color: ${({ theme, isDragging }) =>
    isDragging ? theme.palette.primary.light5 : theme.palette.grayscale.light5};
  transition:
    100ms background-color,
    100ms border;

  &:hover {
    border: 2px dashed
      ${({ theme, isDragging }) =>
        isDragging
          ? theme.palette.primary.main
          : theme.palette.grayscale.light2};
    background-color: ${({ theme, isDragging }) =>
      isDragging
        ? theme.palette.primary.light5
        : theme.palette.grayscale.light5};
  }
`;

const FileInput = styled.input.attrs({
  type: 'file',
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Icon = styled(FontAwesomeIcon).attrs({
  size: 'lg',
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  max-height: 42px;
  color: ${({ theme }) => theme.palette.grayscale.light1};
`;

const Label = styled.div`
  ${Styling.typography('h5')};
  color: ${({ theme }) => theme.palette.grayscale.light1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileList = styled(Box).attrs({
  direction: 'vertical',
})``;

const FileItem = styled(Box).attrs({
  alignX: 'space-between',
  alignY: 'center',
})`
  padding: ${Styling.spacing(1.5)};
  background-color: white;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
  }
`;

const FileName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button.attrs({
  type: 'button',
})`
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms;
  height: 24px;
  width: 24px;
  color: #a5a5a5; // TODO move to theme
  background-color: ${({ theme }) => theme.palette.disabled.main};

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: #a5a5a5; // TODO move to theme
  }
`;

export type FileDropAreaProps = {
  className?: string;
  name?: string;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string[];
  files: File[];
  onChange?(files: File[]): void;
  icon?: IconDefinition;
  label: string;
};

export const FileDropArea: FC<FileDropAreaProps> = ({
  className,
  name,
  disabled,
  multiple,
  accept,
  files,
  onChange,
  icon,
  label,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (files.length === 0) {
      return;
    }

    let dt: DataTransfer;
    try {
      dt = new DataTransfer();
    } catch {
      // DataTransfer does not exist in Safari <=14.0
      return;
    }

    for (const file of files) {
      dt.items.add(file);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.files = dt.files;
    }
  }, []);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    const newFiles = [];

    if (multiple) {
      newFiles.push(...files);
    }

    const fileList = evt.target.files;

    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);

        if (file && !newFiles.includes(file)) {
          newFiles.push(file);
        }
      }
    }

    onChange?.(newFiles);
  };

  const handleDrop: DragEventHandler = (evt) => {
    // Prevent file(s) from being opened
    evt.preventDefault();

    const droppedFiles = Array.from(evt.dataTransfer.files);

    if (!multiple && droppedFiles.length > 0) {
      onChange?.([droppedFiles[0]]);
    } else {
      onChange?.([...files, ...droppedFiles]);
    }

    setIsDragging(false);
  };

  const handleRemove = (index: number): void => {
    const copy = files.slice();
    copy.splice(index, 1);
    onChange?.(copy);
  };

  return (
    <Container>
      <DropArea
        className={className}
        isDragging={isDragging}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDragOver={() => setIsDragging(true)}
        onDrop={handleDrop}
      >
        <Label>{label}</Label>
        <Icon icon={icon ?? faPaperclip} />
        <FileInput
          ref={inputRef}
          name={name}
          disabled={disabled}
          multiple={multiple}
          accept={accept?.join(', ')}
          onChange={handleChange}
        />
      </DropArea>
      {files.length > 0 && (
        <>
          <FileList>
            {files.map((file, index) => (
              <FileItem key={file.name}>
                <FileName>{file.name}</FileName>
                <RemoveButton onClick={() => handleRemove(index)}>
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                </RemoveButton>
              </FileItem>
            ))}
          </FileList>
          <Divider orientation="horizontal" />
        </>
      )}
    </Container>
  );
};
